/**
 * MarMonthlyTable.jsx
 * Shared MAR Monthly Table (UI + Print)
 */

import React from "react";
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";

const normalizeTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;

    const ampm = String(timeStr).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampm) {
        let hour = Number(ampm[1]);
        const minute = Number(ampm[2] || 0);
        const meridiem = ampm[3].toUpperCase();

        if (meridiem === "AM") {
            if (hour === 12) hour = 0;
        } else if (hour !== 12) {
            hour += 12;
        }

        return hour * 60 + minute;
    }

    const twentyFour = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})$/);
    if (twentyFour) {
        return Number(twentyFour[1]) * 60 + Number(twentyFour[2]);
    }

    return null;
};

const getLocalDateParts = (iso) => {
    const raw = String(iso || "").trim();
    const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        return {
            year: Number(dateOnly[1]),
            month: Number(dateOnly[2]) - 1,
            day: Number(dateOnly[3]),
        };
    }

    const d = new Date(iso);
    return {
        year: d.getFullYear(),
        month: d.getMonth(),
        day: d.getDate(),
    };
};

const normalizeStatus = (value, signature = null) => {
    const raw = String(value || "").trim().toUpperCase();
    if (!raw) return "";
    if (raw === "TAKEN" || raw === "ADMINISTERED" || raw === "COMPLETED") return "GIVEN";
    if (raw === "PRN") return "PRN_GIVEN";
    if (raw === "OVERRIDE_PENDING" || raw === "PENDING_OVERRIDE") {
        return signature ? "GIVEN" : "PENDING";
    }
    return raw;
};

const isScheduledForDay = (med, date) => {
    if (!med.schedule) return false;
    if (med.schedule.prn) return false;

    if (med.schedule.everyDay === true) return true;

    if (Array.isArray(med.schedule.daysOfWeek)) {
        return med.schedule.daysOfWeek.includes(date.getDay());
    }

    if (Array.isArray(med.schedule.times) && med.schedule.times.length > 0) {
        return true;
    }

    return false;
};

const isOverdue = (cellDate, timeMinutes) => {
    if (timeMinutes == null) return false;

    const scheduled = new Date(cellDate);
    scheduled.setHours(0, 0, 0, 0);
    scheduled.setMinutes(timeMinutes);
    return scheduled < new Date();
};

const getStatusBadgeStyle = (status) => {
    const normalized = normalizeStatus(status);
    const base = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        borderRadius: "50%",
        fontWeight: 700,
        fontSize: "0.68rem",
        border: "1px solid transparent",
    };

    if (normalized === "GIVEN") {
        return { ...base, backgroundColor: "#dcfce7", color: "#14532d", borderColor: "#22c55e" };
    }
    if (normalized === "PRN_GIVEN") {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e3a8a", borderColor: "#60a5fa" };
    }
    if (normalized === "REFUSED") {
        return { ...base, backgroundColor: "#fde68a", color: "#78350f", borderColor: "#f59e0b" };
    }
    if (normalized === "MISSED" || normalized === "PRN_MISSED") {
        return { ...base, backgroundColor: "#fed7aa", color: "#7c2d12", borderColor: "#fb923c" };
    }
    return { ...base, backgroundColor: "#e5e7eb", color: "#374151", borderColor: "#9ca3af" };
};

const formatRecordedAt = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString([], {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function MarMonthlyTable({
    meds = [],
    doses = [],
    selectedDate,
    forPrint = false,
    onCellClick,
}) {
    if (!selectedDate) return null;

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const cellStyle = {
        border: "1px solid #ccc",
        padding: "4px",
        fontSize: forPrint ? "0.9rem" : "0.85rem",
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={getStatusBadgeStyle("GIVEN")}>AB</Box>
                    <Typography variant="caption">Given</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={getStatusBadgeStyle("PRN_GIVEN")}>AB</Box>
                    <Typography variant="caption">PRN Given</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={getStatusBadgeStyle("REFUSED")}>AB</Box>
                    <Typography variant="caption">Refused</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={getStatusBadgeStyle("MISSED")}>AB</Box>
                    <Typography variant="caption">Missed / PRN Missed</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={getStatusBadgeStyle("PENDING")}>AB</Box>
                    <Typography variant="caption">Pending / Other</Typography>
                </Stack>
            </Stack>

            <TableContainer
                sx={{
                    overflowX: "auto",
                    border: forPrint ? "none" : "1px solid #ddd",
                }}
            >
                <Table size="small" stickyHeader={!forPrint} sx={{ width: "max-content" }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...cellStyle, width: 160, fontWeight: 700 }}>
                            Medication
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, width: 80, fontWeight: 700 }}>
                            Time
                        </TableCell>
                        {days.map((day) => (
                            <TableCell
                                key={day}
                                align="center"
                                sx={{ ...cellStyle, width: 28, fontWeight: 700 }}
                            >
                                {day}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {meds.map((med) => {
                        const medTimes = med.schedule?.times?.length
                            ? med.schedule.times
                            : ["-"];

                        return (
                            <React.Fragment key={med.id}>
                                {medTimes.map((time, index) => {
                                    const timeMinutes = normalizeTimeToMinutes(time);

                                    return (
                                        <TableRow key={`${med.id}-${time}-${index}`}>
                                            {index === 0 && (
                                                <TableCell
                                                    rowSpan={medTimes.length}
                                                    sx={{ ...cellStyle, verticalAlign: "top" }}
                                                >
                                                    <strong>{med.name}</strong>
                                                    {med.dosage && <div>Dosage: {med.dosage}</div>}
                                                    {med.schedule?.prn && (
                                                        <div style={{ color: "#1976d2" }}>
                                                            PRN / As Needed
                                                        </div>
                                                    )}
                                                </TableCell>
                                            )}

                                            <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>
                                                {time}
                                            </TableCell>

                                            {days.map((day) => {
                                                const cellDate = new Date(year, month, day);

                                                const entry = doses.find((x) => {
                                                    if (String(x.medId) !== String(med.id)) return false;

                                                    const entryMinutes = normalizeTimeToMinutes(x.timeOfDay);
                                                    if (entryMinutes != null && timeMinutes != null) {
                                                        if (entryMinutes !== timeMinutes) return false;
                                                    } else {
                                                        const left = String(x.timeOfDay || "").trim().toUpperCase();
                                                        const right = String(time || "").trim().toUpperCase();
                                                        if (left !== right) return false;
                                                    }

                                                    const {
                                                        year: eYear,
                                                        month: eMonth,
                                                        day: eDay,
                                                    } = getLocalDateParts(x.date);

                                                    return (
                                                        eYear === year &&
                                                        eMonth === month &&
                                                        eDay === day
                                                    );
                                                });

                                                let status = normalizeStatus(
                                                    entry?.status,
                                                    entry?.signature
                                                );

                                                if (
                                                    !status &&
                                                    isScheduledForDay(med, cellDate)
                                                ) {
                                                    status = isOverdue(cellDate, timeMinutes)
                                                        ? "MISSED"
                                                        : "PENDING";
                                                }

                                                const statusText = status || "Not recorded";
                                                const signer = String(entry?.signature || "").trim();
                                                const hasScheduleForDay = isScheduledForDay(med, cellDate);
                                                const hasRecordedEntry = Boolean(entry);
                                                const canOpenEditor =
                                                    !forPrint &&
                                                    typeof onCellClick === "function" &&
                                                    String(time || "").trim() !== "-" &&
                                                    (hasScheduleForDay || hasRecordedEntry);
                                                const ymd = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                                const tooltipTitle = [
                                                    `Status: ${statusText}`,
                                                    signer ? `Given by: ${signer}` : "",
                                                    entry?.recordedAt
                                                        ? `Recorded: ${formatRecordedAt(entry.recordedAt)}`
                                                        : "",
                                                ]
                                                    .filter(Boolean)
                                                    .join("\n");

                                                return (
                                                    <TableCell
                                                        key={day}
                                                        align="center"
                                                        onClick={() => {
                                                            if (!canOpenEditor) return;
                                                            onCellClick({
                                                                medId: med.id,
                                                                medName: med.name,
                                                                dosage: med.dosage,
                                                                date: ymd,
                                                                timeOfDay: time,
                                                                status: status || "PENDING",
                                                                signature: entry?.signature || null,
                                                                recordedAt: entry?.recordedAt || null,
                                                                prn: Boolean(med.schedule?.prn),
                                                            });
                                                        }}
                                                        sx={{
                                                            ...cellStyle,
                                                            fontSize: "1.1rem",
                                                            cursor: canOpenEditor ? "pointer" : "default",
                                                            backgroundColor: canOpenEditor ? "#f8fafc" : "transparent",
                                                            "&:hover": canOpenEditor
                                                                ? { backgroundColor: "#eef2ff" }
                                                                : undefined,
                                                        }}
                                                    >
                                                        {signer ? (
                                                            <Tooltip
                                                                title={<span style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</span>}
                                                                arrow
                                                            >
                                                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                                                    <span
                                                                        style={getStatusBadgeStyle(status)}
                                                                    >
                                                                        {signer}
                                                                    </span>
                                                                </span>
                                                            </Tooltip>
                                                        ) : null}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
